import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { ScheduledEvent } from '../../../../../../../models/scheduled-event';
import {
  CallbackResult,
  CallbackResultStatus,
  dateStringFromISO8601,
  getTimeZoneDisplayName,
  timeStringFromISO8601
} from '../../../../../../../utils';
import {
  PrivateEventRequestFormInfoResponse,
  ScheduledEventService
} from '../../../../../../../services/scheduled-event/scheduled-event.service';
import { eventFullDueToSecondaryCapacity, instructorsManuallyEdited } from '../../../../../../../utils-models';
import { CancelEventModalComponent } from '../../../modals/cancel-event-modal/cancel-event-modal.component';
import { SetEventPartInstructorsModalComponent } from '../modals/set-event-part-instructors-modal/set-event-part-instructors-modal.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { EventTypeWaitlistScope, ScheduleType } from '../../../../../../../models/event-type-enums';


enum SubList {
  participants = 'participants',
  waitlist = 'waitlist',
  notes = 'notes',
}


@Component({
  selector: 'app-event-detail',
  templateUrl: './event-detail.component.html',
  styleUrls: ['./event-detail.component.css']
})
export class EventDetailComponent implements OnInit {
  public event: ScheduledEvent = null;
  public subList: SubList = SubList.participants;
  public backUrl: string;

  public privateEventRequestFormInfo: PrivateEventRequestFormInfoResponse;
  public hasNoPrivateEventRequestForm = false;

  public loadingEvent = true;
  public loadingEventPartial = true;
  public loadingPrivateEventRequestFormInfo = false;

  public eventNotFound = false;
  public serverError: number = null;

  // Make browser history available to the template.
  public history: History;

  // The most recent result returned from a modal or sublist.
  public callbackResult: CallbackResult = null;

  // The reservation part to check in once the participants sub-list reloads
  // with its row. Held here because the `*ngIf` wrapper recreates the
  // sub-list on each reload (PRO-1220).
  public pendingCheckInReservationPartId: number | null = null;

  // Whether the participants sub-list shows nonpayment-cancelled reservations.
  // Held here for the same reason; toggling re-fetches the event (PRO-1225).
  public showNonpaymentCancelledReservations = false;

  public readonly eventFullDueToSecondaryCapacity = eventFullDueToSecondaryCapacity;
  public readonly instructorsManuallyEdited = instructorsManuallyEdited;
  public readonly dateStringFromISO8601 = dateStringFromISO8601;
  public readonly timeStringFromISO8601 = timeStringFromISO8601;
  public readonly getTimeZoneDisplayName = getTimeZoneDisplayName;
  public readonly SubList = SubList;
  public readonly CallbackResultStatus = CallbackResultStatus;
  public readonly EventTypeWaitlistScope = EventTypeWaitlistScope;
  public readonly ScheduleType = ScheduleType;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private scheduledEventService: ScheduledEventService,
    private modalService: NgbModal
  ) {}

  ngOnInit() {
    this.route.params.subscribe(routeParams => {
      const eventPartIdInt = parseInt(routeParams.eventPartId, 10);
      if (isNaN(eventPartIdInt)) {
        this.eventNotFound = true;
        this.loadingEvent = false;
        return;
      }

      // Make browser history available to the template.
      this.history = history;

      this.loadEvent(eventPartIdInt);

      this.backUrl = this.router.url.split('/event/')[0];
    });

    const routeFragment = this.route.snapshot.fragment;
    // @ts-ignore
    if (Object.values(SubList).includes(routeFragment)) {
      // @ts-ignore
      this.subList = routeFragment;
    } else {
      this.subList = SubList.participants;
    }
  }

  public handleSubListResult = (result: CallbackResult, reload: boolean) => {
    this.callbackResult = result;
    if (reload) {
      this.loadEvent(this.event.event_part_id, false);
    }
  }

  // The backend includes or omits nonpayment-cancelled reservations by query
  // parameter, so the toggle re-fetches (PRO-1225).
  public handleShowNonpaymentCancelledChange = (value: boolean) => {
    this.showNonpaymentCancelledReservations = value;
    this.loadEvent(this.event.event_part_id, false);
  }

  public selectSubList(subList: SubList) {
    this.subList = subList;

    document.location.hash = subList;

    this.loadEvent(this.event.event_part_id, false);
  }

  public openCancelEventModal() {
    this.callbackResult = null;

    const modalRef = this.modalService.open(CancelEventModalComponent);
    modalRef.componentInstance.eventId = this.event.event_id;
    // For private events, the cancellation modal offers refund options
    // (PRO-1229). It needs the booking ID to load the booking's payment info.
    modalRef.componentInstance.isPrivate = this.event.is_private;
    modalRef.componentInstance.bookingId = this.getPrivateEventBookingId();
    modalRef.componentInstance.savedCallback = () => {
      this.callbackResult = {
        text: 'Event cancelled successfully.',
        status: CallbackResultStatus.success
      };
      this.loadEvent(this.event.event_part_id, true);
    };
  }

  /**
   * The ID of the private event's single booking, taken from its reservations
   * (they all share one booking). Returns null for non-private events or when
   * no booking can be found.
   */
  private getPrivateEventBookingId(): number | null {
    if (!this.event || !this.event.is_private || !this.event.reservations) {
      return null;
    }
    const reservationWithBooking = this.event.reservations.find(
      reservation => !!reservation.booking_id
    );
    return reservationWithBooking ? reservationWithBooking.booking_id : null;
  }

  public openSetEventPartInstructorsModal() {
    this.callbackResult = null;

    const modalRef = this.modalService.open(SetEventPartInstructorsModalComponent);
    modalRef.componentInstance.eventPart = this.event;
    modalRef.componentInstance.savedCallback = () => {
      this.callbackResult = {
        text: 'Instructors updated successfully.',
        status: CallbackResultStatus.success
      };
      this.loadEvent(this.event.event_part_id, true);
    };
  }

  private loadEvent(id: number, setLoading=true) {
    if (setLoading) {
      this.loadingEvent = true;
    }
    this.loadingEventPartial = true;

    this.scheduledEventService
      .getScheduledEvent(id, this.showNonpaymentCancelledReservations)
      .then(result => {
        this.event = result;

        if (this.event.is_private) {
          this.loadPrivateEventRequestFormInfo();
        }
      })
      .catch(reason => {
        if (reason.status === 404) {
          this.eventNotFound = true;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => {
        this.loadingEvent = false;
        this.loadingEventPartial = false;
      });
  }

  private loadPrivateEventRequestFormInfo() {
    if (!this.event || !this.event.is_private) { return; }

    this.loadingPrivateEventRequestFormInfo = true;

    this.scheduledEventService
      .getRequestFormInfoForPrivateEvent(this.event.event_id)
      .then(result => {
        this.privateEventRequestFormInfo = result;
      })
      .catch(reason => {
        if (reason.status === 404) {
          this.hasNoPrivateEventRequestForm = true;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingPrivateEventRequestFormInfo = false; });
  }
}
